<template>
  <div class="time-picker">
    <input
      ref="timePickerInput"
      class="time-picker-input"
      :class="{ 'read-only': readonly, disabled: disabled }"
      v-model.lazy="inputValue"
      v-if="!buttonOnly"
      :readonly="readonly"
      :disabled="disabled"
      :placeholder="placeholder" />
    <button class="time-picker-button" :class="{ disabled: disabled }" :popovertarget="`time-picker-${id}`" ref="timePickerButton" :disabled="disabled">
      <Icon icon="Clock" />
    </button>
    <div
      :id="`time-picker-${id}`"
      class="time-picker-menu"
      :class="{ 'is-open': isOpen }"
      popover
      @toggle="toggleHandler"
      ref="timePickerMenu"
      :style="{
        transform: `translate(${menuPosition.x}px, ${menuPosition.y}px)`
      }">
      <div class="controls hour-controls">
        <b-btn class="add-btn btn-icon mono" @click="changeValue('hour', 1)">
          <Icon icon="ChevronTop" />
        </b-btn>
        <div class="value-label">{{ (vm.localValue ?? moment()).format("HH") }}</div>
        <b-btn class="sub-btn btn-icon mono" @click="changeValue('hour', -1)">
          <Icon icon="ChevronBottom" />
        </b-btn>
      </div>
      <div class="control-divider">:</div>
      <div class="controls minute-controls">
        <b-btn class="add-btn btn-icon mono" @click="changeValue('minute', 1)">
          <Icon icon="ChevronTop" />
        </b-btn>
        <div class="value-label">{{ (vm.localValue ?? moment()).format("mm") }}</div>
        <b-btn class="sub-btn btn-icon mono" @click="changeValue('minute', -1)">
          <Icon icon="ChevronBottom" />
        </b-btn>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Icon from "@common/components/Icon.vue";
import { Guid } from "@util/guid";
import _ from "lodash";
import moment, { Moment, unitOfTime } from "moment";
import { watch } from "vue";
import { computed, nextTick, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";

const DEFAULT_DATE_V_MODEL_FORMAT = "YYYY-MM-DD";
console.debug("DEFAULT_DATE_V_MODEL_FORMAT: ", DEFAULT_DATE_V_MODEL_FORMAT);
const DEFAULT_TIME_V_MODEL_FORMAT = "HH:mm";

//#region General
const { d: $d } = useI18n();

const id = Guid.new();
const timePickerInput = ref<HTMLInputElement | null>(null);
const timePickerButton = ref<HTMLButtonElement | null>(null);
const timePickerMenu = ref<HTMLDivElement | null>(null);

const props = defineProps<{
  value: string | null;
  buttonOnly?: boolean;
  readonly?: boolean;
  disabled?: boolean;
  placeholder?: string;
}>();
const emits = defineEmits(["input"]);

//#endregion

//#region Viewmodel
type ViewModel = { localValue: Moment | null };
const vm = reactive<ViewModel>({ localValue: null });

const menuPosition = reactive({ x: 0, y: 0 });
const isOpen = ref(false);

//#endregion

//#region VisualContruction

const toggleHandler = (event: ToggleEvent) => {
  isOpen.value = event.newState == "open";
  calculatePositionOfMenu();
};

const calculatePositionOfMenu = () => {
  menuPosition.x = 0;
  menuPosition.y = 0;
  nextTick(() => {
    if (timePickerMenu.value && timePickerButton.value) {
      const toggleRect = timePickerButton.value.getBoundingClientRect();
      const menuRect = timePickerMenu.value.getBoundingClientRect();
      const x = toggleRect.x - menuRect.right + toggleRect.width;
      let y = toggleRect.y - menuRect.y + toggleRect.height;
      if (menuRect.bottom + y > window.innerHeight) {
        y = toggleRect.y - menuRect.bottom;
      }
      menuPosition.x = x;
      menuPosition.y = y;
    }
  });
};

//#endregion

//#region LocalValue

watch(
  () => props.value,
  (nv) => {
    if (!_.isEmpty(nv)) vm.localValue = moment(nv, DEFAULT_TIME_V_MODEL_FORMAT);
  },
  { immediate: true }
);

watch(
  () => vm.localValue,
  (nv) => {
    emits("input", nv?.format(DEFAULT_TIME_V_MODEL_FORMAT));
  },
  { deep: true }
);

const changeValue = (unit: unitOfTime.DurationConstructor, value: number) => {
  if (!vm.localValue) vm.localValue = moment().startOf("minutes");
  vm.localValue = moment(vm.localValue.add(value, unit));
  console.debug(vm.localValue?.format(DEFAULT_TIME_V_MODEL_FORMAT));
};

const inputValue = computed({
  get: () => {
    if (vm.localValue && vm.localValue.isValid()) {
      return $d(vm.localValue.toDate(), "shortTime");
    } else return "";
  },
  set: (nv) => {
    if (nv === "") vm.localValue = null;
    else {
      const parsed = moment(`${nv}`, DEFAULT_TIME_V_MODEL_FORMAT);
      if (parsed.isValid()) vm.localValue = parsed;
      else {
        if (timePickerInput.value) {
          timePickerInput.value.value = vm.localValue ? $d(vm.localValue.toDate(), "shortTime") : "";
        }
      }
    }
  }
});

//#endregion
</script>
